import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Display } from '@/types/display';

interface UseNearbyDisplaysOptions {
  latitude: number;
  longitude: number;
  radiusMiles?: number;
  excludeDisplayId?: string;
}

export const useNearbyDisplays = ({ latitude, longitude, radiusMiles = 25, excludeDisplayId }: UseNearbyDisplaysOptions) => {
  return useQuery({
    queryKey: ['nearby-displays', latitude, longitude, radiusMiles, excludeDisplayId],
    queryFn: async (): Promise<{ displays: Display[]; cities: string[] }> => {
      if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        return { displays: [], cities: [] };
      }
      
      // Use the new RPC function instead of problematic PostgREST filter
      let data, error;
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_nearby_displays', {
          center_lat: latitude,
          center_lng: longitude,
          radius_miles: radiusMiles,
          exclude_display_id: excludeDisplayId ? parseInt(excludeDisplayId) : null,
          result_limit: 100
        });
        
        data = rpcData;
        error = rpcError;
      } catch (err) {
        console.error('RPC call failed:', err);
        throw new Error('Failed to fetch nearby displays (RPC error)');
      }
      
      if (error) {
        console.error('Error fetching nearby displays:', error);
        throw new Error(error.message);
      }
      
      // Filter displays that have either an image_url OR associated media
      const filteredDisplays = (data || []).filter(display => {
        // Check if display has a main image
        const hasMainImage = display.image_url && display.image_url.trim() !== '';
        
        // Check if display has associated media through display_years
        const hasAssociatedMedia = display.display_years?.some((year: any) => 
          year.display_media && year.display_media.length > 0
        );
        
        // Show display only if it has either a main image or associated media
        return hasMainImage || hasAssociatedMedia;
      });
      
      const displays: Display[] = filteredDisplays;
      // Deduplicate cities
      const cities = Array.from(new Set(displays.map(d => d.location?.split(',')[0]?.trim()).filter(Boolean)));
      return { displays, cities };
    },
    enabled: typeof latitude === 'number' && typeof longitude === 'number',
  });
}; 